import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API, apiPortraitUrl } from "@/auth";
import BattleStage from "@/components/BattleStage";
import ShareBattleButton from "@/components/ShareBattleButton";
import { useCrowdRoar } from "@/hooks/useCrowdRoar";

// Format seconds as M:SS for the broadcast TIMER HUD
const mmss = (s) => {
 const m = Math.floor(s / 60);
 const r = Math.floor(s % 60);
 return `${m}:${r.toString().padStart(2, "0")}`;
};

/* ============================================================
 /cypher — Launcher: pick opponents + arena or roll random
 /cypher/:id — The cinematic hype reel
 ============================================================ */

export function CypherLauncher() {
 const nav = useNavigate();
 const [roster, setRoster] = useState([]);
 const [arenas, setArenas] = useState([]);
 const [aiA, setAiA] = useState(null);
 const [aiB, setAiB] = useState(null);
 const [arena, setArena] = useState(null);
 const [recent, setRecent] = useState([]);
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");

 useEffect(() => {
 Promise.all([
 axios.get(`${API}/battles/ai-roster`),
 axios.get(`${API}/arenas`),
 axios.get(`${API}/cyphers/recent?limit=6`),
 ]).then(([r, a, c]) => { setRoster(r.data); setArenas(a.data); setRecent(c.data); })
 .catch(() => {});
 }, []);

 const start = async (random = false) => {
 setBusy(true); setErr("");
 try {
 const payload = random ? {} : {
 opponent_a_key: aiA?.key,
 opponent_b_key: aiB?.key,
 arena_key: arena?.key,
 };
 const { data } = await axios.post(`${API}/cypher/create`, payload);
 nav(`/cypher/${data.id}`);
 } catch (e) {
 setErr(e.response?.data?.detail || "Couldn't start cypher");
 } finally { setBusy(false); }
 };

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> AI vs AI · MARKETING REEL</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-3">
 THE <span className="text-yt-red">CYPHER</span>
 </h1>
 <p className="text-yt-muted font-mono text-xs tracking-widest mb-8 max-w-2xl">
 WATCH TWO AI RAPPERS GO HEAD TO HEAD. FULL VOCALS. LIVE JUDGING. PERFECT FOR SCREEN-RECORDING AND SHARING.
 </p>

 {/* Big red "ROLL RANDOM" button */}
 <div className="bg-yt-red border-4 border-yt-yellow p-6 md:p-10 shadow-brutal mb-6">
 <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6">
 <div className="md:col-span-2">
 <div className="font-mono text-xs tracking-[0.4em] text-black mb-2">▶ QUICK START</div>
 <div className="font-display text-4xl md:text-5xl tracking-tight text-white">ROLL A RANDOM CYPHER</div>
 <div className="font-mono text-sm text-white/80 mt-2">
 Random opponents. Random arena. 60-90 sec to cook. Then screen-record and share.
 </div>
 </div>
 <button data-testid="roll-random-btn" onClick={() => start(true)} disabled={busy}
 className="bg-black text-yt-yellow font-display text-2xl tracking-wider px-8 py-5 border-4 border-yt-yellow hover:bg-yt-yellow hover:text-black transition-colors shadow-brutal disabled:opacity-40">
 {busy ? "COOKING..." : "ROLL"}
 </button>
 </div>
 </div>

 {/* Pitch Battle — soccer diss battle on The Pitch */}
 <div className="bg-black border-4 border-yt-yellow p-6 md:p-8 shadow-brutal mb-10 relative overflow-hidden">
 <div className="absolute inset-0 opacity-20 pointer-events-none"
 style={{ backgroundImage: "url(/arenas/pitch.png)", backgroundSize: "cover", backgroundPosition: "center" }} />
 <div className="relative grid grid-cols-1 md:grid-cols-3 items-center gap-6">
 <div className="md:col-span-2">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-2">⚽ SOCCER · PITCH BATTLE</div>
 <div className="font-display text-3xl md:text-5xl tracking-tight text-white">
 SETTLE IT ON <span className="text-yt-yellow">THE PITCH.</span>
 </div>
 <div className="font-mono text-sm text-white/80 mt-2 max-w-lg">
 Two AI rappers cut each other down over who's the worse footballer. Air-kicks, own goals, Sunday-league beef — 8 bars a piece on The Pitch under halftime lights.
 </div>
 </div>
 <button data-testid="roll-pitch-battle-btn"
 onClick={async () => {
 setBusy(true); setErr("");
 try {
 const { data } = await axios.post(`${API}/cypher/pitch-battle`);
 nav(`/cypher/${data.id}`);
 } catch (e) {
 setErr(e.response?.data?.detail || "Couldn't start Pitch Battle");
 } finally { setBusy(false); }
 }}
 disabled={busy}
 className="bg-yt-yellow text-black font-display text-2xl tracking-wider px-8 py-5 border-4 border-black hover:bg-white transition-colors shadow-brutal disabled:opacity-40">
 {busy ? "COOKING..." : "⚽ PITCH BATTLE"}
 </button>
 </div>
 </div>

 {/* Custom matchup */}
 <details className="mb-10 border border-yt-border bg-yt-surface p-4">
 <summary className="font-mono text-xs tracking-widest text-yt-muted cursor-pointer hover:text-yt-yellow">
 ⚙ CUSTOM MATCHUP (PICK YOUR OPPONENTS + ARENA)
 </summary>
 <div className="mt-4 space-y-4">
 <RosterRow label="RAPPER A" roster={roster} value={aiA} onChange={setAiA} testPrefix="a" />
 <RosterRow label="RAPPER B" roster={roster.filter(r => r.key !== aiA?.key)} value={aiB} onChange={setAiB} testPrefix="b" />
 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">ARENA</div>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
 {arenas.map(a => (
 <button key={a.key} data-testid={`cypher-arena-${a.key}`} onClick={() => setArena(a)}
 className={`aspect-video overflow-hidden border ${arena?.key === a.key ? "border-yt-yellow" : "border-yt-border hover:border-yt-yellow"}`}>
 <img src={a.image} alt={a.name} className="w-full h-full object-cover" />
 </button>
 ))}
 </div>
 </div>
 <button data-testid="cypher-custom-start-btn" onClick={() => start(false)} disabled={busy || !aiA || !aiB}
 className="w-full bg-yt-yellow text-black font-display text-xl tracking-wider py-3 disabled:opacity-40 shadow-brutal">
 {busy ? "COOKING..." : "▶ START CUSTOM CYPHER"}
 </button>
 </div>
 </details>

 {err && <div data-testid="cypher-err" className="mb-6 font-mono text-xs text-yt-red border border-yt-red/40 p-3">{err}</div>}

 {/* Recent cyphers */}
 {recent.length > 0 && (
 <>
 <div className="font-mono text-xs tracking-[0.4em] text-yt-muted mb-4"> RECENT CYPHERS</div>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {recent.map(c => (
 <Link key={c.id} to={`/cypher/${c.id}`} data-testid={`recent-cypher-${c.id}`}
 className="bg-yt-surface border border-yt-border hover:border-yt-yellow overflow-hidden transition-colors">
 <div className="aspect-video bg-black overflow-hidden relative">
 <img src={c.arena?.image} alt={c.arena?.name} className="w-full h-full object-cover opacity-60" />
 <div className="absolute inset-0 flex items-center justify-around p-4 gap-2">
 <CypherFace ai={c.ai_a} winner={c.winner_key === c.ai_a?.key} />
 <div className="font-display text-4xl text-white">VS</div>
 <CypherFace ai={c.ai_b} winner={c.winner_key === c.ai_b?.key} />
 </div>
 </div>
 <div className="p-3">
 <div className="font-mono text-[10px] text-yt-muted tracking-widest">{c.arena?.name?.toUpperCase()}</div>
 <div className="font-display text-lg">{c.ai_a?.name} vs {c.ai_b?.name}</div>
 <div className="font-mono text-xs text-yt-yellow mt-1">WINNER: {c.winner_name || "—"}</div>
 </div>
 </Link>
 ))}
 </div>
 </>
 )}
 </div>
 );
}

function CypherFace({ ai, winner }) {
 return (
 <div className={`w-20 h-20 rounded-full overflow-hidden border-2 ${winner ? "border-yt-yellow" : "border-white/40"}`}>
 {ai?.portrait ? (
 <img src={apiPortraitUrl(ai.portrait)} alt={ai.name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-lg text-yt-yellow">{(ai?.name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 </div>
 );
}

function RosterRow({ label, roster, value, onChange, testPrefix }) {
 return (
 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">{label}</div>
 <div className="grid grid-cols-4 gap-2">
 {roster.map(ai => (
 <button key={ai.key} data-testid={`cypher-${testPrefix}-${ai.key}`} onClick={() => onChange(ai)}
 className={`aspect-square overflow-hidden border ${value?.key === ai.key ? "border-yt-yellow ring-2 ring-yt-yellow" : "border-yt-border hover:border-yt-yellow"}`}>
 {ai.portrait ? (
 <img src={apiPortraitUrl(ai.portrait)} alt={ai.name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-lg text-yt-yellow">{(ai.name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 </button>
 ))}
 </div>
 </div>
 );
}

/* ============================================================
 /cypher/:id — THE CINEMATIC HYPE REEL
 Screen-record this. Fullscreen. Autoplays. Ends with CTA.
 ============================================================ */

export function CypherReel() {
 const { id } = useParams();
 const [cypher, setCypher] = useState(null);
 const [phase, setPhase] = useState("intro"); // intro | face_off | play_a | interlude | play_b | verdict | outro
 // Karaoke sync: derive the current line from the ACTIVE audio element's
 // currentTime / duration instead of a naive setInterval. Keeps lyrics in
 // lock-step with the vocalist (user reported previous ticker jumped ahead).
 const [audioTime, setAudioTime] = useState(0);
 const [audioDuration, setAudioDuration] = useState(0);
 const [muted, setMuted] = useState(true); // start muted so autoplay works, unmute via CTA
 const [phaseElapsed, setPhaseElapsed] = useState(0); // seconds since current phase started
 const audioARef = useRef(null);
 const audioBRef = useRef(null);
 const audioJudgeRef = useRef(null);
 const crowd = useCrowdRoar({ volume: 0.05 });

 // Unmute all media elements on user gesture (browser autoplay-with-sound restriction)
 // CRITICAL: we ONLY flip .muted to false here — we do NOT call .play() on both
 // rapper audio elements. Doing that caused both rappers to sing at the same
 // time. The phase-driven useEffect below is the single source of truth for
 // which audio track is currently playing (turn-based).
 const unmuteAll = () => {
 setMuted(false);
 for (const el of [audioARef.current, audioBRef.current, audioJudgeRef.current]) {
 if (el) { el.muted = false; }
 }
 // Only KICK the audio element that matches the current phase — never both.
 if (phase === "play_a" && audioARef.current && cypher?.audio_a && !cypher?.lipsync_a) {
 audioARef.current.play().catch(() => {});
 } else if (phase === "play_b" && audioBRef.current && cypher?.audio_b && !cypher?.lipsync_b) {
 audioBRef.current.play().catch(() => {});
 } else if (phase === "verdict" && audioJudgeRef.current && cypher?.judge_audio) {
 audioJudgeRef.current.play().catch(() => {});
 }
 // Videos on stage are the muted stadium backdrop + (optional) lipsync
 // MP4s. Only the CURRENTLY-rapping fighter's video should be unmuted —
 // otherwise both fighters' lipsyncs play with audio in parallel.
 document.querySelectorAll('video').forEach(v => {
 // Stadium ambient / motion loops stay muted forever. Only the lipsync
 // video on the ACTIVE side becomes audible; others stay silenced even
 // if they were auto-unmuted before this fix.
 const isActiveLipsync = v.dataset?.stageRole === "lipsync-active";
 if (isActiveLipsync) { v.muted = false; }
 v.play().catch(() => {});
 });
 // Kick off crowd roar ambient — Web Audio requires the user gesture we
 // just got from this click.
 crowd.start();
 };

 // Poll for cypher status until completed
 useEffect(() => {
 let iv;
 const load = async () => {
 try {
 const { data } = await axios.get(`${API}/cypher/${id}`);
 setCypher(data);
 if (data.status === "completed") {
 clearInterval(iv);
 }
 } catch { /* pass */ }
 };
 load();
 iv = setInterval(load, 6000);
 return () => clearInterval(iv);
 }, [id]);

 // Kick off the reel once we have a completed cypher
 useEffect(() => {
 if (!cypher || cypher.status !== "completed") return;
 // Reset phase to intro on first data arrival
 setPhase("intro");
 const timers = [];
 timers.push(setTimeout(() => setPhase("face_off"), 3500));
 timers.push(setTimeout(() => setPhase("play_a"), 7500));
 return () => timers.forEach(clearTimeout);
 }, [cypher?.status]);

 // Broadcast timer — tick every second and reset on phase change so the
 // HUD TIMER field counts down for real during play_a / play_b instead of
 // showing a static "1:00".
 useEffect(() => {
 setPhaseElapsed(0);
 if (!["play_a", "play_b", "verdict"].includes(phase)) return;
 const iv = setInterval(() => setPhaseElapsed(e => e + 1), 1000);
 return () => clearInterval(iv);
 }, [phase]);

 // Autoplay + sequence audio elements. When a lipsync video is available for
 // this side, we skip playing the separate audio element (the lipsync MP4 has
 // the audio baked in already) — otherwise you'd hear the verse twice.
 //
 // CRITICAL: hard-stop BOTH rap audio tracks on every phase change so we can
 // never have "both rappers rapping at the same time."One mic at a time.
 useEffect(() => {
 if (!cypher || cypher.status !== "completed") return;
 // Pause everything first — guarantees turn-based playback.
 for (const el of [audioARef.current, audioBRef.current]) {
 if (el) { try { el.pause(); el.currentTime = 0; } catch { /* noop */ } }
 }
 if (phase === "play_a" && cypher.audio_a && audioARef.current && !cypher.lipsync_a) {
 audioARef.current.play().catch(() => {
 setTimeout(() => setPhase("interlude"), 30000);
 });
 }
 if (phase === "play_b" && cypher.audio_b && audioBRef.current && !cypher.lipsync_b) {
 audioBRef.current.play().catch(() => {
 setTimeout(() => setPhase("verdict"), 30000);
 });
 }
 if (phase === "verdict" && cypher.judge_audio && audioJudgeRef.current) {
 audioJudgeRef.current.play().catch(() => {
 setTimeout(() => setPhase("outro"), 8000);
 });
 }
 if (phase === "play_a" && !cypher.audio_a) {
 // No audio → wait 10s reading verse then advance
 setTimeout(() => setPhase("interlude"), 12000);
 }
 if (phase === "play_b" && !cypher.audio_b) {
 setTimeout(() => setPhase("verdict"), 12000);
 }
 // Safety-net: even with lipsync video, advance after 45s in case
 // onEnded misfires (mobile Safari sometimes swallows it).
 if (phase === "play_a" && cypher.lipsync_a) {
 const t = setTimeout(() => setPhase(p => p === "play_a" ? "interlude" : p), 45000);
 return () => clearTimeout(t);
 }
 if (phase === "play_b" && cypher.lipsync_b) {
 const t = setTimeout(() => setPhase(p => p === "play_b" ? "verdict" : p), 45000);
 return () => clearTimeout(t);
 }
 if (phase === "interlude") {
 setTimeout(() => setPhase("play_b"), 2500);
 }
 if (phase === "outro") {
 // Stay on outro
 }
 }, [phase, cypher?.status]);

 // Reset audio-time state whenever the active phase changes so the ticker
 // starts on line 1 for each rapper's turn.
 useEffect(() => {
 setAudioTime(0);
 setAudioDuration(0);
 }, [phase]);

 if (!cypher) {
 return (
 <div className="fixed inset-0 bg-black flex items-center justify-center text-yt-yellow font-mono">
 LOADING CYPHER...
 </div>
 );
 }

 if (cypher.status !== "completed") {
 const statusMsg = {
 queued: "Rolling the dice...",
 generating_verses: "AIs writing bars...",
 generating_audio: "Suno cooking vocals (60-120 sec)...",
 judging: "Judge deliberating...",
 }[cypher.status] || "Preparing...";
 return (
 <div className="fixed inset-0 bg-black flex items-center justify-center flex-col text-center p-8">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> CYPHER IN PROGRESS</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight text-white mb-6">
 {cypher.ai_a?.name} <span className="text-yt-red">vs</span> {cypher.ai_b?.name}
 </h1>
 <div className="font-mono text-yt-yellow text-lg animate-pulse-red mb-8">{statusMsg}</div>
 <div className="w-64 h-2 bg-yt-surface overflow-hidden">
 <div className="h-full bg-yt-yellow animate-pulse-red" style={{ width: "60%" }} />
 </div>
 <div className="font-mono text-xs text-yt-muted mt-8 max-w-md">
 Auto-refreshes every 6 sec. Hang tight — total time is usually 90-150 sec.
 </div>
 </div>
 );
 }

 const lines = phase === "play_a"
 ? (cypher.verse_a || "").split("\n").filter(l => l.trim())
 : (cypher.verse_b || "").split("\n").filter(l => l.trim());
 // Audio-time-driven caption index. If we don't yet know the duration
 // (metadata not loaded) fall back to a soft 4.5s pace so we don't freeze
 // on line 1. Once duration lands, we snap to the exact ratio.
 let captionIdx = 0;
 if (lines.length > 0) {
 if (audioDuration > 0 && isFinite(audioDuration)) {
 captionIdx = Math.min(
 lines.length - 1,
 Math.floor((audioTime / audioDuration) * lines.length),
 );
 } else {
 captionIdx = Math.min(lines.length - 1, Math.floor(phaseElapsed / 4.5));
 }
 }
 const activeLine = lines[captionIdx] || "";
 const activeRapper = phase === "play_a" ? cypher.ai_a : cypher.ai_b;

 return (
 <div data-testid="cypher-reel" className="fixed inset-0 bg-black overflow-hidden z-50">
 {/* Arena backdrop */}
 <img src={cypher.arena?.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
 <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/90" />

 {/* Hidden audio elements. onTimeUpdate + onLoadedMetadata feed the
 karaoke caption bar so lyric lines stay in lock-step with the vocals. */}
 {cypher.audio_a && (
 <audio ref={audioARef} src={cypher.audio_a} preload="auto"
 onEnded={() => setPhase("interlude")}
 onLoadedMetadata={(e) => { if (phase === "play_a") setAudioDuration(e.currentTarget.duration || 0); }}
 onTimeUpdate={(e) => { if (phase === "play_a") setAudioTime(e.currentTarget.currentTime || 0); }} />
 )}
 {cypher.audio_b && (
 <audio ref={audioBRef} src={cypher.audio_b} preload="auto"
 onEnded={() => setPhase("verdict")}
 onLoadedMetadata={(e) => { if (phase === "play_b") setAudioDuration(e.currentTarget.duration || 0); }}
 onTimeUpdate={(e) => { if (phase === "play_b") setAudioTime(e.currentTarget.currentTime || 0); }} />
 )}
 {cypher.judge_audio && (
 <audio ref={audioJudgeRef} src={cypher.judge_audio}
 onEnded={() => setPhase("outro")} />
 )}

 {/* === PHASE: INTRO === Big splash */}
 {phase === "intro" && (
 <div className="absolute inset-0 flex flex-col items-center justify-center animate-fadein">
 <div className="font-mono text-xs tracking-[0.6em] text-yt-red mb-4 animate-pulse-red"> AI CYPHER · LIVE</div>
 <div className="font-display text-8xl md:text-9xl tracking-tight text-white leading-none">
 YOUNG <span className="text-yt-red">TONGUES</span>
 </div>
 <div className="font-mono text-lg text-yt-yellow tracking-widest mt-4">RAP BATTLE PLATFORM</div>
 </div>
 )}

 {/* === PHASE: FACE OFF === Both rappers reveal — full-body on stage */}
 {phase === "face_off" && (
 <BattleStage
 arenaImage={cypher.arena?.image}
 arenaName={cypher.arena?.name}
 round={1}
 centerCue="START TO SPIT"
 fighterA={{ name: cypher.ai_a?.name, portrait: cypher.ai_a?.portrait, motion: cypher.ai_a?.motion, lipsync: null, rapping: false }}
 fighterB={{ name: cypher.ai_b?.name, portrait: cypher.ai_b?.portrait, motion: cypher.ai_b?.motion, lipsync: null, rapping: false }}
 verseLine={null}
 judgeComment={null}
 purseLeft="PURSE: $5"
 purseRight="EXTRA ROUND: $2"
 muted={muted}
 onUnmute={unmuteAll}
 timerLeft="1:00"
 timerRight="1:00"
 extraTopRight={null}
 />
 )}

 {/* === PHASE: PLAY A / PLAY B === Live face-off with lipsync video swap */}
 {(phase === "play_a" || phase === "play_b") && (
 <BattleStage
 arenaImage={cypher.arena?.image}
 arenaName={cypher.arena?.name}
 round={1}
 centerCue={null}
 fighterA={{
 name: cypher.ai_a?.name,
 portrait: cypher.ai_a?.portrait,
 motion: cypher.ai_a?.motion,
 lipsync: cypher.lipsync_a,
 rapping: phase === "play_a",
 onEnded: () => setPhase("interlude"),
 }}
 fighterB={{
 name: cypher.ai_b?.name,
 portrait: cypher.ai_b?.portrait,
 motion: cypher.ai_b?.motion,
 lipsync: cypher.lipsync_b,
 rapping: phase === "play_b",
 onEnded: () => setPhase("verdict"),
 }}
 verseLine={activeLine}
 judgeComment={null}
 purseLeft="PURSE: $5"
 purseRight="EXTRA ROUND: $2"
 muted={muted}
 onUnmute={unmuteAll}
 timerLeft={mmss(Math.max(0, 60 - phaseElapsed))}
 timerRight={mmss(Math.max(0, 60 - phaseElapsed))}
 extraTopRight={
 <div className="flex flex-col gap-2 items-end">
 <ShareBattleButton compact highlightEndpoint={`/cypher/${id}/highlight-mp4`} />
 <MP4DownloadButton cypherId={id} winner={cypher.winner_name} compact />
 </div>
 }
 />
 )}

 {/* === PHASE: INTERLUDE === */}
 {phase === "interlude" && (
 <div className="absolute inset-0 flex items-center justify-center animate-fadein">
 <div className="font-display text-6xl md:text-8xl text-yt-red animate-pulse-red">
 {cypher.ai_b?.name}<br/>
 <span className="text-white">CLAPS BACK</span>
 </div>
 </div>
 )}

 {/* === PHASE: VERDICT === Judge speaks */}
 {phase === "verdict" && (
 <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center animate-fadein">
 <div className="font-mono text-xs tracking-[0.6em] text-yt-red mb-4 animate-pulse-red"> VERDICT</div>
 <div className="font-display text-4xl md:text-6xl text-yt-judge mb-6">
 JUDGE DEGAGNE
 </div>
 <div className="bg-black/80 border-2 border-yt-judge px-8 py-6 max-w-3xl scanlines">
 <div className="font-mono text-lg md:text-2xl text-yt-judge leading-relaxed">
 {cypher.judge_verdict}
 </div>
 </div>
 <div className="grid grid-cols-2 gap-8 mt-8">
 <ScoreCard ai={cypher.ai_a} judge={cypher.judge_a} winner={cypher.winner_key === cypher.ai_a?.key} />
 <ScoreCard ai={cypher.ai_b} judge={cypher.judge_b} winner={cypher.winner_key === cypher.ai_b?.key} />
 </div>
 </div>
 )}

 {/* === PHASE: OUTRO === Call to action */}
 {phase === "outro" && (
 <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center animate-fadein">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> WINNER</div>
 <div className="font-display text-6xl md:text-8xl text-yt-yellow mb-2">
 {cypher.winner_name}
 </div>
 <div className="font-mono text-yt-muted text-lg mt-8 mb-6">TAKES THE CYPHER</div>
 <div className="border-t-4 border-yt-red pt-8 mt-4">
 <div className="font-display text-4xl md:text-6xl text-white mb-2">
 THINK YOU CAN <span className="text-yt-red">DO BETTER?</span>
 </div>
 <div className="font-mono text-yt-yellow text-2xl md:text-4xl tracking-widest mt-4">
 youngtongues.com
 </div>
 <div className="font-mono text-xs text-yt-muted tracking-[0.4em] mt-3">
 STEP INTO THE CYPHER · GET JUDGED · CROWNED CHAMPIONS
 </div>
 <div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-3 flex-wrap">
 <Link
 to="/battles/new"
 data-testid="cypher-battle-again-btn"
 className="bg-yt-red text-white font-display text-lg tracking-wider px-6 py-3 hover:bg-yt-yellow hover:text-black shadow-brutal shadow-brutal-hover"
 >
 ▶ BATTLE AGAIN
 </Link>
 <Link
 to="/"
 data-testid="cypher-back-home-btn"
 className="border-2 border-yt-yellow text-yt-yellow font-display text-lg tracking-wider px-6 py-3 hover:bg-yt-yellow hover:text-black shadow-brutal"
 >
 ◀ BACK TO HOME
 </Link>
 <ShareBattleButton highlightEndpoint={`/cypher/${id}/highlight-mp4`} />
 <MP4DownloadButton cypherId={id} winner={cypher.winner_name} />
 </div>
 </div>
 </div>
 )}

 {/* Persistent brand watermark bottom-right */}
 <div className="absolute bottom-4 right-4 font-mono text-xs tracking-widest text-white/60 bg-black/60 px-3 py-1">
 youngtongues.com
 </div>

 {/* Persistent MP4 download button — always visible so users can grab the video anytime */}
 <div className="absolute top-4 right-4 z-40 flex flex-col gap-2 items-end">
 {muted && phase !== "intro" && (
 <button data-testid="cypher-reel-unmute" onClick={unmuteAll}
 className="bg-yt-yellow text-black font-mono text-xs tracking-widest px-3 py-2 shadow-brutal hover:bg-white">
 UNMUTE
 </button>
 )}
 <MP4DownloadButton cypherId={id} winner={cypher.winner_name} compact />
 </div>

 {/* Screen record instructions — only visible in intro */}
 {phase === "intro" && (
 <div className="absolute bottom-6 left-6 max-w-sm bg-yt-yellow text-black px-4 py-3 font-mono text-xs animate-pulse-red">
 RECORD THIS!<br/>
 iPhone: Control Center → Record button<br/>
 Android: Quick Settings → Screen Record
 </div>
 )}
 </div>
 );
}

function FaceOffPortrait({ ai, rapping, lipsyncUrl, muted, onEnded }) {
 const videoRef = useRef(null);

 // When it becomes this rapper's turn AND we have a lipsynced MP4, play it.
 // Otherwise fall back to the static portrait. The lipsync video already has
 // baked-in audio so no separate audio playback happens when it's driving.
 useEffect(() => {
 const v = videoRef.current;
 if (!v) return;
 if (rapping && lipsyncUrl) {
 v.currentTime = 0;
 v.muted = !!muted;
 // Attempt to play; browsers block audio autoplay without gesture so
 // this may fail silently — fallback is a muted play in FaceOffPortrait's
 // JSX props on the <video> tag.
 v.play().catch(() => {
 // Retry muted so at least the mouth animates
 v.muted = true;
 v.play().catch(() => {});
 });
 } else {
 try { v.pause(); } catch { /* noop */ }
 }
 }, [rapping, lipsyncUrl, muted]);

 const showVideo = !!lipsyncUrl;
 // Idle-bob when idle, subtle glow when rapping
 const idleAnim = !rapping ? "animate-idle-bob" : "";

 return (
 <div className={`relative mx-auto ${rapping ? "ring-4 ring-yt-yellow" : "ring-2 ring-white/30"} ${idleAnim}`}
 style={{ width: "min(280px, 26vw)", height: "min(360px, 34vw)" }}>
 {showVideo ? (
 <video ref={videoRef} src={lipsyncUrl} playsInline autoPlay={rapping} muted={muted || !rapping}
 loop={!rapping}
 onEnded={rapping ? onEnded : undefined}
 className="w-full h-full object-cover" preload="metadata" />
 ) : ai?.portrait ? (
 <img src={apiPortraitUrl(ai.portrait)} alt={ai?.name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-6xl text-yt-yellow">{(ai?.name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 {rapping && (
 <div className="absolute inset-0 pointer-events-none">
 <div className="absolute inset-0 ring-4 ring-yt-yellow animate-pulse" />
 <div className="absolute text-4xl drop-shadow-[0_0_8px_rgba(255,215,0,0.9)] animate-mic-raise"
 style={{ bottom: "45%", right: "22%" }}></div>
 </div>
 )}
 <div className="absolute -bottom-8 left-0 right-0 text-center">
 <div className="inline-block bg-black/90 px-3 py-1 border-2 border-white/30">
 <div className="font-display text-2xl md:text-3xl text-white leading-none">{ai?.name?.toUpperCase()}</div>
 <div className="font-mono text-[9px] text-yt-yellow tracking-widest">{ai?.style}</div>
 </div>
 </div>
 </div>
 );
}

function ScoreCard({ ai, judge, winner }) {
 return (
 <div className={`bg-black/70 border-2 ${winner ? "border-yt-yellow" : "border-yt-border"} p-4`}>
 <div className="font-display text-2xl md:text-3xl text-white mb-2">
 {ai?.name} {winner && <span className="text-yt-yellow">★</span>}
 </div>
 <div className="grid grid-cols-5 gap-1 mb-2">
 {[["FLO", judge?.flow], ["RHY", judge?.rhyme], ["TPC", judge?.topic], ["PCH", judge?.punchlines], ["CRD", judge?.crowd]].map(([k, v]) => (
 <div key={k} className="border border-yt-border bg-black py-1 text-center">
 <div className="font-display text-lg text-yt-yellow leading-none">{v ?? 0}</div>
 <div className="font-mono text-[8px] text-yt-muted tracking-widest">{k}</div>
 </div>
 ))}
 </div>
 <div className="font-mono text-sm text-yt-judge">{judge?.total ?? 0}/100</div>
 </div>
 );
}

/* ============================================================
 MP4 Download Button — hits /api/cypher/:id/mp4. First press
 triggers a server-side stitch (~30-90s), then delivers file.
 ============================================================ */
function MP4DownloadButton({ cypherId, winner, compact }) {
 const [state, setState] = useState("idle"); // idle | building | ready | error
 const [err, setErr] = useState("");
 const pollRef = useRef(null);

 // Probe once to see if the MP4 is already cached
 useEffect(() => {
 axios.get(`${API}/cypher/${cypherId}/mp4-status`)
 .then(r => { if (r.data?.status === "ready") setState("ready"); })
 .catch(() => {});
 return () => { if (pollRef.current) clearInterval(pollRef.current); };
 }, [cypherId]);

 const startBuild = async () => {
 setErr("");
 setState("building");
 try {
 await axios.post(`${API}/cypher/${cypherId}/mp4/build`);
 } catch (e) {
 setErr(e.response?.data?.detail || e.message);
 setState("error");
 return;
 }
 // Poll every 4s for up to ~4 min
 let ticks = 0;
 pollRef.current = setInterval(async () => {
 ticks += 1;
 try {
 const r = await axios.get(`${API}/cypher/${cypherId}/mp4-status`);
 if (r.data?.status === "ready") {
 clearInterval(pollRef.current);
 setState("ready");
 // Auto-trigger download
 fetchAndDownload();
 } else if (r.data?.status?.startsWith("failed")) {
 clearInterval(pollRef.current);
 setErr(r.data.status.replace(/^failed:/, ""));
 setState("error");
 }
 } catch { /* keep polling */ }
 if (ticks > 60) {
 clearInterval(pollRef.current);
 setErr("Timed out — try again");
 setState("error");
 }
 }, 4000);
 };

 const fetchAndDownload = async () => {
 try {
 const resp = await fetch(`${API}/cypher/${cypherId}/mp4`, { credentials: "include" });
 if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
 const blob = await resp.blob();
 const w = (winner || "cypher").replace(/[^a-z0-9]/gi, "_");
 const a = document.createElement("a");
 const url = URL.createObjectURL(blob);
 a.href = url;
 a.download = `youngtongues_${w}_${cypherId.slice(-6)}.mp4`;
 document.body.appendChild(a); a.click(); a.remove();
 URL.revokeObjectURL(url);
 } catch (e) {
 setErr(e.message || "Download failed");
 setState("error");
 }
 };

 const onClick = () => {
 if (state === "ready") return fetchAndDownload();
 if (state !== "building") return startBuild();
 };

 const label = state === "building" ? "STITCHING..."
 : state === "ready" ? "⬇ DOWNLOAD MP4"
 : state === "error" ? "⬇ TRY AGAIN"
 : "⬇ MP4";

 if (compact) {
 return (
 <button
 data-testid="cypher-mp4-download-compact"
 onClick={onClick}
 disabled={state === "building"}
 title="Server stitches this battle into a shareable 9:16 MP4"
 className={`px-3 py-2 font-mono text-[10px] tracking-widest border-2 shadow-brutal
 ${state === "building"
 ? "border-yt-yellow text-yt-yellow bg-black animate-pulse-red cursor-wait"
 : "border-yt-yellow text-black bg-yt-yellow hover:bg-white"}`}
 >
 {label}
 </button>
 );
 }

 return (
 <div className="mt-8">
 <button
 data-testid="cypher-mp4-download"
 onClick={onClick}
 disabled={state === "building"}
 className={`inline-flex items-center gap-3 px-6 py-4 font-display text-xl tracking-wider border-4 border-yt-yellow shadow-brutal
 ${state === "building"
 ? "bg-black text-yt-yellow animate-pulse-red cursor-wait"
 : "bg-yt-yellow text-black hover:bg-white"}`}
 >
 {state === "building"
 ? "STITCHING YOUR VIDEO... (30-90 sec)"
 : state === "ready" ? "⬇ DOWNLOAD MP4"
 : state === "error" ? "⬇ TRY AGAIN"
 : "⬇ DOWNLOAD MP4"}
 </button>
 <div className="font-mono text-[10px] text-white/70 tracking-widest mt-2">
 SERVER STITCHES THE FULL BATTLE INTO A 9:16 CLIP — POST IT ANYWHERE.
 </div>
 {err && <div className="font-mono text-xs text-yt-red mt-2">{err}</div>}
 </div>
 );
}
